import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { NewButtonWidgetModel } from 'models';
import { CaseLevel1Code, CaseLevel2Code } from 'shared/constants/common-cases';

import services from 'services';

import { ThemeProvider, theme } from 'configs/theme-configurator';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useDispatch from 'shared/hooks/useDispatch';
import useTranslation from 'shared/hooks/useTranslation';

import getCaseLevels2BasedOnCaseLevel1 from 'shared/utils/common-cases/getCaseLevels2BasedOnCaseLevel1';
import caseLevelsAdapter from 'shared/utils/common-cases/caseLevelsAdapter';

import { KeyOrderCreateForm } from 'pages/key-orders';
import { setCaseLevels } from 'pages/incident/slice';
import { UserCommonCaseCreateForm } from 'pages/incident';
import { UserSpaceRenovationRequestCreateForm } from 'pages/repair';

import Button from '@mui/material/Button';
import CaseMenu, { CaseMenuOption } from 'components/controls/case-menu';

interface CaseCreatorWidgetProps {
  defaultWidgetData?: NewButtonWidgetModel;
}

const CaseCreatorWidget = ({ defaultWidgetData }: CaseCreatorWidgetProps) => {
  const { t } = useTranslation();

  const anchorRef = useRef(null);
  const coopId = useCooperativeId();
  const dispatch = useDispatch();
  const history = useHistory();

  const [widgetData, setWidgetData] = useState(() => defaultWidgetData);
  const [widgetDataLoading, setWidgetDataLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [createFormOpen, setCreateFormOpen] = useState({
    incident: false,
    repair: false,
    keyorder: false,
  });

  useEffect(() => {
    if (!defaultWidgetData) {
      (async function () {
        try {
          setWidgetDataLoading(true);

          const widgetData: NewButtonWidgetModel | undefined = (
            await services.get(
              `/api/user/cooperatives/${coopId}/widget/newbutton`
            )
          ).data?.Data;

          if (widgetData) {
            setWidgetData(widgetData);
            setWidgetDataLoading(false);
          }
        } catch (err) {
          console.error(err);
          setWidgetDataLoading(false);
        }
      })();
    }
  }, [defaultWidgetData, coopId]);

  const handleToggleFormView = (
    entity: 'incident' | 'repair' | 'keyorder',
    flag: boolean
  ) => {
    setCreateFormOpen((prevState) => ({
      ...prevState,
      [entity]: flag,
    }));

    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  const handleOpenIncidentForm = () => handleToggleFormView('incident', true);
  const handleCloseIncidentForm = () => {
    handleToggleFormView('incident', false);
  };
  const handleOpenKeyOrderForm = () => handleToggleFormView('keyorder', true);
  const handleCloseKeyOrderForm = () => handleToggleFormView('keyorder', false);
  const handleOpenRepairForm = () => handleToggleFormView('repair', true);
  const handleCloseRepairForm = () => handleToggleFormView('repair', false);
  const forwardToServicesPage = () => history.push('/services');

  const handleMenuOpen = () => {
    setMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
  };

  const selectCases = (
    caseLevel1: CaseLevel1Code,
    caseLevel2: CaseLevel2Code
  ) => {
    dispatch(
      setCaseLevels({
        caseLevel1,
        caseLevel2,
      })
    );

    handleMenuClose();
    handleOpenIncidentForm();
  };

  const options: CaseMenuOption[] = useMemo(() => {
    return [
      {
        id: 'inicdent',
        label: 'Palvelupyyntö',
        visible: !!widgetData?.CanCreateCase,
        children: widgetData?.CaseLevels1
          ? caseLevelsAdapter(widgetData.CaseLevels1, (caseLevel1) => ({
              children: widgetData?.CaseLevels2
                ? caseLevelsAdapter(
                    getCaseLevels2BasedOnCaseLevel1(
                      caseLevel1.Value,
                      widgetData.CaseLevels2
                    ),
                    (caseLevel2) => ({
                      children: [],
                      action: () =>
                        selectCases(caseLevel1.Value, caseLevel2.Value),
                    })
                  )
                : [],
            }))
          : [],
      },
      {
        id: 'repair',
        label: 'Muutostyöilmoitus',
        children: [],
        visible: !!widgetData?.CanCreateRapair,
        action: handleOpenRepairForm,
      },
      {
        id: 'key-order',
        label: 'Avaintilaus',
        children: [],
        visible: !!widgetData?.CanCreateKeyOrder,
        action: handleOpenKeyOrderForm,
      },
      {
        id: 'services',
        label: 'Varauspalvelut',
        children: [],
        visible: !!widgetData?.CanCreateReservation,
        action: forwardToServicesPage,
      },
    ];
  }, [widgetData]);

  const showButton =
    widgetData &&
    (widgetData.CanCreateCase ||
      widgetData.CanCreateKeyOrder ||
      widgetData.CanCreateRapair ||
      widgetData.CanCreateReservation);

  if (!showButton) return null;

  // TODO: Carry out ThemeProvider
  return (
    <ThemeProvider theme={theme}>
      <Button
        disabled={widgetDataLoading || !widgetData}
        color="secondary"
        ref={anchorRef}
        onClick={handleMenuOpen}
        sx={{ fontWeight: 600 }}
      >
        {t('NewButtonLabel')}
      </Button>
      <CaseMenu
        open={menuOpen}
        options={options}
        handleClose={handleMenuClose}
        anchorEl={anchorRef.current}
      />
      {createFormOpen.incident && (
        <UserCommonCaseCreateForm onClose={handleCloseIncidentForm} />
      )}
      {createFormOpen.keyorder && (
        <KeyOrderCreateForm onClose={handleCloseKeyOrderForm} />
      )}
      {createFormOpen.repair && (
        <UserSpaceRenovationRequestCreateForm onClose={handleCloseRepairForm} />
      )}
    </ThemeProvider>
  );
};

export default memo(CaseCreatorWidget);
